import type { NumberLocale } from "../core/parser/locale";
import { parseAmount, parseDate } from "../core/parser/locale";
import type { BalanceInfo, BankTemplate, ColumnLayout } from "../core/parser/template";
import { textInXRange } from "../core/pdf/lines";
import type { PositionedText, TextLine, Transaction } from "../core/types";

// ALTA Banka a.d. Beograd — corporate daily-turnover statement
// ("IZVOD ZA PROMENU SREDSTAVA NA RAČUNU"). A fundamentally different, block-
// structured document from the personal current-account statement (see
// altaBankaRs.ts): each payment order ("nalog") spans several lines, anchored by
// an order number 1..N in the far-left column, with its amount in either the
// "zaduženje" (debit) or "odobrenje" (credit) sub-column. Parsed with a bespoke
// parseTransactions hook rather than the generic column engine.
//
// Numbers are US-style ("259,655.70" = comma thousands / dot decimal).

export const ALTA_CORP_LOCALE: NumberLocale = {
  decimalSeparator: ".",
  thousandsSeparator: ",",
};

// Horizontal guides (PDF points), from a real statement.
const X = {
  orderNo: { min: 30, max: 48 }, // "br." order number (block anchor)
  name: { min: 48, max: 215 }, // recipient name / address / account
  date: { min: 218, max: 288 }, // Datum knjiženja (top) / Datum prijema
  amount: { min: 300, max: 430 }, // zaduženje | odobrenje amounts
  purpose: { min: 450, max: 625 }, // Svrha doznake
  // Amounts right-align to ~357 (zaduženje) or ~425 (odobrenje); split above this:
  creditRightEdge: 365,
  // Header "STANJE" values row: opening (Prethodno stanje) | closing (Novo stanje).
  openingCol: { min: 30, max: 145 },
  closingCol: { min: 305, max: 385 },
};

// Layout is unused by the bespoke parser but required by the interface.
const CORP_COLUMNS: ColumnLayout = {
  date: { xMin: X.date.min, xMax: X.date.max },
  description: { xMin: X.name.min, xMax: X.name.max },
  amount: { xMin: X.amount.min, xMax: X.amount.max },
};

export const altaCorpRsTemplate: BankTemplate = {
  id: "rs-alta-corp",
  name: "ALTA Banka a.d. Beograd",
  country: "RS",
  currency: "RSD",
  numberLocale: ALTA_CORP_LOCALE,
  dateFormats: ["DD.MM.YYYY"],
  layout: CORP_COLUMNS,
  dateCellPattern: /^\d{2}\.\d{2}\.\d{4}$/,

  detect(fullText: string): number {
    const t = fullText.toLowerCase();
    let score = 0;
    if (t.includes("alta banka")) score += 0.4;
    // "IZVOD BR. N" and "ZA PROMENU SREDSTAVA" sit on separate lines, so match
    // the phrase that actually appears contiguously.
    if (t.includes("za promenu sredstava")) score += 0.6;
    return Math.min(score, 1);
  },

  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo {
    const full = lines.map((l) => l.text).join("\n");

    // Opening/closing are the first row carrying a number in BOTH the
    // "Prethodno stanje" and "Novo stanje" columns.
    let opening: number | null = null;
    let closing: number | null = null;
    for (const line of lines) {
      const op = parseAmount(textInXRange(line, X.openingCol.min, X.openingCol.max), locale);
      const cl = parseAmount(textInXRange(line, X.closingCol.min, X.closingCol.max), locale);
      if (op != null && cl != null) {
        opening = op;
        closing = cl;
        break;
      }
    }

    const date = /\bDANA\s+(\d{2}\.\d{2}\.\d{4})/i.exec(full);
    const iso = date ? parseDate(date[1]!, ["DD.MM.YYYY"]) ?? undefined : undefined;
    const acc =
      /ukupno za ra[cč]un\s*(\d{3}-\d+-\d+)/i.exec(full) ?? /(\d{3}-\d{13}-\d{2})/.exec(full);

    return {
      opening,
      closing,
      periodStart: iso,
      periodEnd: iso,
      accountId: acc ? acc[1] : undefined,
    };
  },

  parseTransactions(lines: TextLine[]): Transaction[] {
    const loc = ALTA_CORP_LOCALE;
    const center = (it: PositionedText) => it.x + it.width / 2;
    const inCol = (it: PositionedText, c: { min: number; max: number }) =>
      center(it) >= c.min && center(it) < c.max;

    // 0) Bound the transaction region to the "PROMENE" section: below its column
    //    header (the "… zaduženje odobrenje" row) and above the "Ukupno" totals.
    //    Otherwise block 1 would swallow the header STANJE amounts above it.
    const promeneY = lines.find((l) => /^\s*PROMENE\b/i.test(l.text))?.y ?? Infinity;
    const headerLine = lines
      .filter((l) => l.y < promeneY && /odobrenje/i.test(l.text) && /(zadu[žz]enje|datum prijema)/i.test(l.text))
      .sort((a, b) => b.y - a.y)[0];
    const promeneTop = headerLine ? headerLine.y : promeneY;
    const promeneBottom =
      lines
        .filter((l) => /ukupno (za ra[cč]un|dinara)/i.test(l.text))
        .sort((a, b) => b.y - a.y)[0]?.y ?? -Infinity;

    // 1) Order-number anchors (one per nalog), top-to-bottom.
    const anchors: number[] = [];
    for (const line of lines) {
      if (line.y >= promeneTop || line.y <= promeneBottom) continue;
      for (const it of line.items)
        if (inCol(it, X.orderNo) && /^\d{1,3}$/.test(it.text.trim())) anchors.push(it.y);
    }
    anchors.sort((a, b) => b - a);
    if (anchors.length === 0) return [];

    const stmtDate = (() => {
      const m = /\bDANA\s+(\d{2}\.\d{2}\.\d{4})/i.exec(lines.map((l) => l.text).join("\n"));
      return m ? parseDate(m[1]!, ["DD.MM.YYYY"]) : null;
    })();

    const txs: Transaction[] = [];
    for (let i = 0; i < anchors.length; i++) {
      const upper = i === 0 ? promeneTop : (anchors[i - 1]! + anchors[i]!) / 2;
      const lower =
        i === anchors.length - 1 ? promeneBottom : (anchors[i]! + anchors[i + 1]!) / 2;
      const blockItems = lines
        .filter((l) => l.y > lower && l.y <= upper && l.y < promeneTop && l.y > promeneBottom)
        .flatMap((l) => l.items);

      // Amount = topmost purely-numeric item in the amount band (skips
      // "Obr. naknada:" fee labels, which carry letters).
      const amtItems = blockItems
        .filter((it) => inCol(it, X.amount) && /^-?[\d.,]+$/.test(it.text.trim()))
        .sort((a, b) => b.y - a.y);
      const amtItem = amtItems[0];
      if (!amtItem) continue;
      const mag = parseAmount(amtItem.text, loc);
      if (mag == null) continue;
      const isCredit = amtItem.x + amtItem.width > X.creditRightEdge;
      const amount = isCredit ? Math.abs(mag) : -Math.abs(mag);

      // Dates: booking (topmost) then value date.
      const dateItems = blockItems
        .filter((it) => inCol(it, X.date) && /^\d{2}\.\d{2}\.\d{4}\.?$/.test(it.text.trim()))
        .sort((a, b) => b.y - a.y);
      const bookIso = dateItems[0]
        ? parseDate(dateItems[0].text.replace(/\.$/, ""), ["DD.MM.YYYY"])
        : null;
      const valIso = dateItems[1]
        ? parseDate(dateItems[1].text.replace(/\.$/, ""), ["DD.MM.YYYY"])
        : null;

      const joinCol = (c: { min: number; max: number }) =>
        blockItems
          .filter((it) => inCol(it, c))
          .sort((a, b) => b.y - a.y || a.x - b.x)
          .map((it) => it.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
      const description = [joinCol(X.name), joinCol(X.purpose)]
        .filter(Boolean)
        .join(" — ");

      const tx: Transaction = {
        date: bookIso ?? stmtDate ?? "",
        description,
        amount,
      };
      if (valIso) tx.valueDate = valIso;
      txs.push(tx);
    }
    return txs;
  },
};
